import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft, FileText } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router'

import {
  type CharterStatsPayload,
  CharterStatus,
  type CharterSummary,
  type UpdateCharterRequest,
} from '@ankor-io/common/types/charter'

import { useCharter } from '@/hooks/useCharter'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Heading } from '@/ui-components/Heading'
import Loading from '@/ui-components/Loading'
import FilesAndDocuments from '@/views/CharterDetail/FilesAndDocuments'
import GratuityCalculator from '@/views/CharterSignOff/GratuityCalculator'
import SignOffConfirmationModal from '@/views/CharterSignOff/SignOffConfirmationModal'

const CharterSignOffPage: React.FC = () => {
  const { charterId } = useParams()
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { charter, setCharter } = useCharter()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [stats, setStats] = useState<CharterStatsPayload | null>(null)
  const [isSigningOffCharter, setIsSigningOffCharter] = useState(false)
  const [gratuityAmountCents, setGratuityAmountCents] = useState<number | null>(null)
  const [isSignOffConfirmationModalOpen, setIsSignOffConfirmationModalOpen] = useState(false)

  useEffect(() => {
    if (!charterId || !activeOrganizationId || !activeConnectedAccountId) {
      return
    }

    const fetchData = async () => {
      try {
        const token = await getAccessToken()
        const baseUrl = `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charterId}`
        const [charterRes, statsRes] = await Promise.all([
          fetch(baseUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${baseUrl}/stats`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        ])

        if (!charterRes.ok) {
          throw new Error('Failed to fetch charter')
        }

        if (!statsRes.ok) {
          throw new Error('Failed to fetch stats')
        }

        const [charterData, statsData] = await Promise.all([
          charterRes.json() as Promise<CharterSummary>,
          statsRes.json() as Promise<CharterStatsPayload>,
        ])

        setCharter(charterData)
        setStats(statsData)
      } catch (error) {
        console.error('Error fetching charter:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charterId, activeOrganizationId, activeConnectedAccountId])

  if (isLoading) {
    return <Loading />
  }

  if (!charter) {
    return <div>Charter not found</div>
  }

  const handleSignOff = () => {
    const signOffCharter = async () => {
      if (!charter) {
        throw new Error('Charter not found')
      }

      // Currently with the dropdown field it closes upon clicking a button inside of it, so we won't really see this disabled but if the users internet is slow and go back into the menu, this can prevent it
      setIsSigningOffCharter(true)

      try {
        // Convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: UpdateCharterRequest = {
          ...charter,
          status: CharterStatus.SIGNING_OFF,
        }

        // TODO: Add gratuity expense
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter/${charter.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'PUT',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to sign off charter.')
        }

        const data: CharterSummary = await res.json()
        setCharter(data)
        setIsSignOffConfirmationModalOpen(false)
        navigate(`/charters/${data.id}`)
      } catch (error) {
        console.error('Error signing off charter:', error)
        throw new Error('Failed to sign off charter.')
      } finally {
        setIsSigningOffCharter(false)
      }
    }

    toast.promise(signOffCharter, {
      loading: 'Signing off charter...',
      success: 'Charter signed off successfully.',
      error: 'Failed to sign off charter.',
    })
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        aria-label="Go back"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <div className="space-y-6">
        <Heading level={3}>Charter Sign-Off</Heading>

        {/* Generate Sign-Off Document */}
        <div className="flex items-start sm:items-center flex-col sm:flex-row gap-3 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex-1 flex items-center gap-x-2">
            <FileText className="size-5" />
            <div className="flex-1">
              <Heading level={6}>Generate Sign-Off Document</Heading>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Generate a PDF summary of all charter expenses for client review and approval.
              </p>
            </div>
          </div>
          <Button
            className="w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            color="primary"
            disabled={isLoading}
          >
            Generate PDF (wire me up)
          </Button>
        </div>

        {/* Files and documents */}
        <FilesAndDocuments charterId={charter.id} />

        {/* Gratuity Calculator */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <GratuityCalculator
            charter={charter}
            stats={stats}
            onChange={setGratuityAmountCents}
          />
        </div>

        <div className="flex justify-end">
          <Button
            className="disabled:opacity-50 disabled:cursor-not-allowed"
            color="primary"
            disabled={gratuityAmountCents === null || isSigningOffCharter}
            onClick={() => setIsSignOffConfirmationModalOpen(true)}
          >
            Charter Sign-off
          </Button>
        </div>
      </div>

      <SignOffConfirmationModal
        isOpen={isSignOffConfirmationModalOpen}
        onClose={() => setIsSignOffConfirmationModalOpen(false)}
        onConfirm={handleSignOff}
      />
    </>
  )
}

export default CharterSignOffPage
